import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Download, History } from 'lucide-react';
import { Banner } from '@/components/Banner';
import { Button } from '@/components/Button';
import { EstadoVacio } from '@/components/Card';
import {
  ETIQUETAS_MOTIVO,
  leerCopia,
  listarCopias,
  nombreArchivoCopia,
  type MotivoCopia,
  type ResumenCopia,
} from '@/db/copias';
import { describirUltimoRespaldo } from '@/domain/backup-reminder';
import { descargarArchivo } from '@/lib/file-transfer';
import { formatearBytes } from '@/lib/storage';
import { BotonDeshacer } from './BotonDeshacer';

/** Descarga una copia interna como archivo de respaldo normal. */
export async function descargarCopia(motivo: MotivoCopia): Promise<boolean> {
  const copia = await leerCopia(motivo);
  if (copia === undefined) return false;
  descargarArchivo(nombreArchivoCopia(copia), copia.contenido);
  return true;
}

function FilaCopia({ copia }: { copia: ResumenCopia }) {
  const [error, setError] = useState<string | null>(null);
  const fecha = describirUltimoRespaldo(copia.creadaEn);

  return (
    <li className="space-y-3 py-4">
      <div className="flex items-start gap-[13px]">
        <span className="grid w-8 shrink-0 place-items-center pt-0.5 text-marca">
          <History className="size-5" />
        </span>
        <div className="grid min-w-0 gap-[3px]">
          <p className="text-[14px] font-medium text-texto">{ETIQUETAS_MOTIVO[copia.motivo]}</p>
          <p className="text-[12px] text-texto-suave">
            {fecha} · {copia.perfil} · {copia.totalRegistros} registros ·{' '}
            {formatearBytes(copia.bytes)}
          </p>
        </div>
      </div>
      <Button
        variante="secundario"
        icono={Download}
        onClick={() => {
          setError(null);
          void descargarCopia(copia.motivo).then((ok) => {
            if (!ok) setError('La copia ya no existe.');
          });
        }}
      >
        Descargar esta copia
      </Button>
      {copia.motivo === 'antes_de_restaurar' && (
        <BotonDeshacer fechaCopia={fecha} registrosCopia={copia.totalRegistros} />
      )}
      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo descargar">
          {error}
        </Banner>
      )}
    </li>
  );
}

/**
 * Copias internas del teléfono (Req. 6.10, 6.11, 2.10): la de antes de la
 * última restauración y la de antes de la última actualización de datos.
 * No son un respaldo: viven en el mismo teléfono.
 */
export function CopiasInternas() {
  const copias = useLiveQuery(() => listarCopias(), []);

  if (copias === undefined) return null;
  if (copias.length === 0) {
    return (
      <EstadoVacio
        icono={History}
        titulo="Todavía no hay copias internas"
        descripcion="Se crea una antes de cada restauración, para poder deshacerla. Conserva también un respaldo fuera de DuoFit."
      />
    );
  }
  return (
    <ul className="divide-y divide-borde">
      {copias.map((copia) => (
        <FilaCopia key={copia.id} copia={copia} />
      ))}
    </ul>
  );
}
